import request from "../../utils/request";
import { Result } from "../../models/general";

/* ───────────────────────── Types ───────────────────────── */

export type PlatformTermsKind = "GeneralUsage" | "TrialUpgrade" | "Privacy";
export type PlatformTermsStatus = "Draft" | "Published";

export interface PlatformTermsView {
  id: number;
  kind: PlatformTermsKind | number;
  version: number;
  title: string;
  contentMarkdown: string;
  status: PlatformTermsStatus | number;
  effectiveFrom?: string | null;
  publishedAt?: string | null;
  publishedByUserId?: string | null;
  publishedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlatformTermsAcceptanceView {
  id: number;
  termsId: number;
  kind: PlatformTermsKind | number;
  version: number;
  title?: string;
  acceptedAt: string;
  userId?: string;
  userName?: string;
}

export interface PlatformTermsDraftInput {
  kind: PlatformTermsKind | number;
  title: string;
  contentMarkdown: string;
  effectiveFrom?: string | null;
}

/* ────────────── Enum coercion (numbers ↔ strings) ────────────── */

const KIND_NAMES: PlatformTermsKind[] = ["GeneralUsage", "TrialUpgrade", "Privacy"];
const STATUS_NAMES: PlatformTermsStatus[] = ["Draft", "Published"];

export function coerceKind(
  value: PlatformTermsKind | number | string | undefined | null,
): PlatformTermsKind {
  if (typeof value === "number") return KIND_NAMES[value] ?? "GeneralUsage";
  if (typeof value === "string") {
    if ((KIND_NAMES as string[]).includes(value)) return value as PlatformTermsKind;
    // Handle numeric strings ("0", "1", "2")
    const n = Number(value);
    if (!Number.isNaN(n) && KIND_NAMES[n]) return KIND_NAMES[n];
  }
  return "GeneralUsage";
}

export function coerceTermsStatus(
  value: PlatformTermsStatus | number | string | undefined | null,
): PlatformTermsStatus {
  if (typeof value === "number") return STATUS_NAMES[value] ?? "Draft";
  if (typeof value === "string") {
    if ((STATUS_NAMES as string[]).includes(value)) return value as PlatformTermsStatus;
    const n = Number(value);
    if (!Number.isNaN(n) && STATUS_NAMES[n]) return STATUS_NAMES[n];
  }
  return "Draft";
}

/** Send the wire-friendly string form to the server. The backend accepts either,
 *  but strings keep network traces readable. */
export function kindToWire(kind: PlatformTermsKind | number | string): string {
  return coerceKind(kind);
}

/* ───────────────────────── Endpoints ───────────────────────── */

// PUBLIC — anonymous fetch of the current published version for a kind
export async function getCurrentTerms(kind: PlatformTermsKind | number | string) {
  const response = await request.get<Result<PlatformTermsView>>(
    `PlatformTerms/current/${kindToWire(kind)}`,
  );
  return response.data;
}

// Authenticated — accept a specific terms record
export async function acceptTerms(id: number) {
  const response = await request.post<Result<{ success: boolean }>>(
    `PlatformTerms/${id}/accept`,
    {},
  );
  return response.data;
}

// Authenticated — list of kinds the current user has not accepted the current version of
export async function getPendingTerms() {
  const response = await request.get<Result<(PlatformTermsKind | number | string)[]>>(
    `PlatformTerms/pending`,
  );
  return response.data;
}

// Authenticated — the user's own acceptances (for their account page)
export async function getMyAcceptances() {
  const response = await request.get<Result<PlatformTermsAcceptanceView[]>>(
    `PlatformTerms/my-acceptances`,
  );
  return response.data;
}

// Operator — list all versions (optionally scoped to one kind)
export async function listTerms(kind?: PlatformTermsKind | number | string) {
  const qs = kind !== undefined && kind !== null ? `?kind=${kindToWire(kind)}` : "";
  const response = await request.get<Result<PlatformTermsView[]>>(`PlatformTerms${qs}`);
  return response.data;
}

// Operator — fetch a single version by id
export async function getTerms(id: number) {
  const response = await request.get<Result<PlatformTermsView>>(`PlatformTerms/${id}`);
  return response.data;
}

// Operator — create a new draft
export async function createDraft(input: PlatformTermsDraftInput) {
  const body = { ...input, kind: kindToWire(input.kind) };
  const response = await request.post<Result<PlatformTermsView>>(
    `PlatformTerms/draft`,
    body,
  );
  return response.data;
}

// Operator — update an existing draft (only allowed while Draft)
export async function updateDraft(id: number, input: PlatformTermsDraftInput) {
  const body = { ...input, kind: kindToWire(input.kind) };
  const response = await request.put<Result<PlatformTermsView>>(
    `PlatformTerms/${id}/draft`,
    body,
  );
  return response.data;
}

// Operator — publish a draft (forces re-acceptance on next login)
export async function publishTerms(id: number) {
  const response = await request.post<Result<PlatformTermsView>>(
    `PlatformTerms/${id}/publish`,
    {},
  );
  return response.data;
}

/* ───────── Free-tier signup + Start Trial ───────── */

export interface SignUpFreeInput {
  company: {
    name: string;
    countryId: number;
    phoneNumber: string;
    timeZone: string;
  };
  user: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    password: string;
    countryId: number;
  };
  acceptedTerms: boolean;
  /** Optional attribution slug from `?ref=<slug>` on the signup URL. */
  referenceSlug?: string | null;
}

export async function signupFree(input: SignUpFreeInput) {
  const response = await request.post<Result<{ id?: number }>>(
    `Company/SignupFree`,
    input,
  );
  return response.data;
}

// CO-227 — startTrial() and StartTrialInput removed with POST Company/StartTrial.
// ⚠️ PlatformTermsKind.TrialUpgrade is deliberately NOT removed from the kind union: historical
// PlatformTermsAcceptance rows reference it and the operator terms console must still render them.

/* ───────── Helpers ───────── */

export function termsKindLabel(kind: PlatformTermsKind): string {
  switch (kind) {
    case "GeneralUsage":
      return "General Usage Terms";
    case "TrialUpgrade":
      return "Trial Upgrade Terms";
    case "Privacy":
      return "Privacy Policy";
  }
}
